import { ng } from "../ngds";

/*
Sometimes the server returns an error object that looks like this:

{
    "type":"https://tools.ietf.org/html/rfc7231#section-6.5.1",
    "title":"One or more validation errors occurred.",
    "status":400,
    "traceId":"00-a5164d410157ddd4f05882bd70ee0bfc-0afac5af7483cb44-00",
    "errors": {
        "":["Multiple values specified for oneof element"]
    }
}

The error is sometimes a string like this:

"Unknown field: foobar"

This function both attempts to act as documentation and checks if the object looks like an error object.
*/
export const jsonLooksLikeError = (o: any): boolean => {
  if (o.type && o.title && o.status && o.traceId && o.errors) {
    return true;
  }
  if (typeof o == "string" && o.startsWith("Unknown field")) {
    return true;
  }
  return false;
};

// if label is missing the error shouldnt be displayed to the user
// values: the log entry's structured values, kept so a caller can show WHERE the error is.
// The concrete engine sends the position as one value with an EMPTY key (ForwardConcMessages in
// sx-conc-design Control.cpp), which renders as just "x = 6.870 m"; a value WITH a key renders as
// "key : value". Dropping these was why sx-column showed a yielding error with no location while
// sx-beam showed one.
export type CalcError = { label?: string; text: string; values?: ng.log.LogValue[] };

export class ErrorException extends Error {
  ng_errors: CalcError[];
  constructor(errors: CalcError[]) {
    super(errors.map((x) => x.text).join(" | "));
    this.ng_errors = errors;
  }
}
export type NgErrorPayload = {
  ng_errors: CalcError[];
};

const hasNgErrors = (x: unknown): x is { ng_errors: CalcError[] } => {
  if (!x || typeof x !== "object") return false;
  const ng_errors = (x as { ng_errors?: unknown }).ng_errors;
  if (!Array.isArray(ng_errors)) return false;

  return ng_errors.every((e) => !!e && typeof e === "object" && typeof (e as { text?: unknown }).text === "string");
};

export const extractErrors = (x: unknown): null | CalcError[] => {
  if (!(x instanceof ErrorException) && !hasNgErrors(x)) {
    return null;
  }

  return x.ng_errors;
};

export const throwIfErrors = (errors: CalcError[]) => {
  if (errors.length === 0) return;
  throw new ErrorException(errors);
};

// Per the log contract (Utils/log.proto), a calc log entry is meant for the end
// user only when it is typed as an error AND carries a `label` (the localisation
// key). Entries that have only `text` are internal debugging ("not intended for
// end user") and are intentionally NOT surfaced to the client. The analyser
// always responds HTTP 200, so these entries can ride inside a successful
// response — callers must scan the project log explicitly, not rely on status.
export const clientErrorsFromProjectLog = (projData: ng.project.Data | null | undefined): CalcError[] => {
  const entries = getLogErrors(projData?.log);
  return depulicateErrors(entries);
};

// The calc can log the same labelled error more than once (e.g. once per stage or
// load combination). De-duplicate identical (label, text) entries so the client
// surfaces each distinct error a single time.
const depulicateErrors = (entries: CalcError[]): CalcError[] => {
  const seen = new Set<string>();
  return entries.filter((e) => {
    // The values are part of the identity: the engine sends the SAME label and text for every
    // position and distinguishes them only by the x it attaches, so keying on label+text alone
    // collapsed yielding at two positions into one message.
    const key = `${e.label}\u0000${e.text}\u0000${formatCalcErrorValues(e)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const getLogErrors = (log: ng.log.Log | null | undefined): CalcError[] => {
  return (
    log?.entries
      ?.filter((e) => e.type === ng.log.LogType.LOG_TYPE_ERROR)
      .map((e) => ({ label: e.label, text: e.text, values: e.values })) ?? []
  );
};

// Render a CalcError's values the way sx-beam's inspectLog does: an empty key contributes the
// value alone, a named key contributes "key : value". Returns "" when there is nothing to add.
export const formatCalcErrorValues = (err: CalcError): string => {
  const parts = (err.values ?? [])
    .map((v) => {
      const prefix = v.key ? `${v.key} : ` : "";
      switch (v.value?.type) {
        case "boolValue":
          return prefix + (v.value.boolValue ? "true" : "false");
        case "integerValue":
          return prefix + v.value.integerValue;
        case "stringValue":
          return prefix + v.value.stringValue;
        case "doubleValue":
          return prefix + v.value.doubleValue;
        default:
          return "";
      }
    })
    .filter((s) => s !== "");
  return parts.length > 0 ? `(${parts.join(", ")})` : "";
};

/// join many sources to a single source
export const joinLogErrors = (errors: CalcError[][]): CalcError[] => {
  return depulicateErrors(errors.flat());
};
